import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import { ArrowLeft, Camera, Trash, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  company_name: "", company_name_ar: "",
  first_name: "", last_name: "",
  email: "", phone: "",
  city: "", city_ar: "", country: "",
  hero_message: "", hero_message_ar: "",
  bio: "", bio_ar: "",
  website: "", linkedin: "",
  service_tags: [], custom_tag: "",
};

export default function CreateAdvisorProfile({ editMode = false }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dir = lang === "ar" ? "rtl" : "ltr";

  const [form, setForm]           = useState(emptyForm);
  const [predefined, setPredefined] = useState([]);
  const [imageFile, setImageFile]   = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existing, setExisting]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bootstrapping, setBoot]    = useState(true);
  const fileRef = useRef(null);

  const token = useMemo(() => localStorage.getItem("esg_token"), []);
  const auth  = { headers: token ? { Authorization: `Bearer ${token}` } : {} };

  useEffect(() => {
    (async () => {
      try {
        const [p, me] = await Promise.all([
          axios.get(`${API}/advisors/predefined-tags`, auth),
          axios.get(`${API}/advisors/me`,              auth),
        ]);
        setPredefined(p.data.tags || []);
        const prof = me.data.profile;
        if (prof) {
          setExisting(prof);
          setForm({
            company_name:    prof.company_name    || "",
            company_name_ar: prof.company_name_ar || "",
            first_name:   prof.first_name   || "",
            last_name:    prof.last_name    || "",
            email:        prof.email        || user?.email || "",
            phone:        prof.phone        || "",
            city:         prof.city         || "",
            city_ar:      prof.city_ar      || "",
            country:      prof.country      || "",
            hero_message:    prof.hero_message    || "",
            hero_message_ar: prof.hero_message_ar || "",
            bio:          prof.bio          || "",
            bio_ar:       prof.bio_ar       || "",
            website:      prof.website      || "",
            linkedin:     prof.linkedin     || "",
            service_tags: prof.service_tags || [],
            custom_tag:   "",
          });
          if (prof.image_url) setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${prof.image_url}`);
        } else if (user?.email) {
          setForm(f => ({ ...f, email: user.email }));
        }
      } catch (e) {
        /* non-fatal */
      } finally {
        setBoot(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTag = (t) => {
    setForm(f => ({
      ...f,
      service_tags: f.service_tags.includes(t)
        ? f.service_tags.filter(x => x !== t)
        : [...f.service_tags, t],
    }));
  };
  const addCustomTag = () => {
    const v = (form.custom_tag || "").trim();
    if (!v) return;
    if (form.service_tags.includes(v)) { set("custom_tag", ""); return; }
    setForm(f => ({ ...f, service_tags: [...f.service_tags, v], custom_tag: "" }));
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error(t("advisors_image_too_big")); return; }
    setImageFile(f);
    const r = new FileReader();
    r.onload = () => setImagePreview(r.result);
    r.readAsDataURL(f);
  };

  // Which key the form field writes to depends on the current UI language.
  // AR locale writes to the `_ar` suffix fields; EN locale writes to base.
  const K = (base) => (lang === "ar" ? `${base}_ar` : base);

  const validate = () => {
    const req = ["first_name", "last_name", "email", "phone", "country"];
    const localeReq = [K("company_name"), K("city"), K("hero_message"), K("bio")];
    for (const k of [...req, ...localeReq]) {
      if (!String(form[k] || "").trim()) {
        const label = k.replace(/_ar$/, "");
        toast.error(t("advisors_missing_field") + ": " + t(`advisors_f_${label}`));
        return false;
      }
    }
    if (!editMode && !imageFile) { toast.error(t("advisors_missing_image")); return false; }
    if (form.service_tags.length === 0) { toast.error(t("advisors_pick_tag")); return false; }
    if (String(form[K("bio")] || "").trim().length < 20) { toast.error(t("advisors_bio_short")); return false; }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      delete payload.custom_tag;
      // Tell the backend which language the user typed in. The backend
      // re-translates the opposite side from this source on every save,
      // so profiles stay in sync without the user ever managing both
      // languages manually.
      payload.source_lang = lang;
      // Seed the opposite-language fields from the source so Pydantic's
      // min_length validators pass. The backend will overwrite them
      // with a proper translation (see _fill_translations in advisors.py).
      if (lang === "ar") {
        payload.company_name = payload.company_name || payload.company_name_ar;
        payload.city         = payload.city         || payload.city_ar;
        payload.hero_message = payload.hero_message || payload.hero_message_ar;
        payload.bio          = payload.bio          || payload.bio_ar;
      } else {
        payload.company_name_ar = payload.company_name_ar || payload.company_name;
        payload.city_ar         = payload.city_ar         || payload.city;
        payload.hero_message_ar = payload.hero_message_ar || payload.hero_message;
        payload.bio_ar          = payload.bio_ar          || payload.bio;
      }
      const fd = new FormData();
      fd.append("payload", JSON.stringify(payload));
      if (imageFile) fd.append("image", imageFile);
      const url = editMode ? `${API}/advisors/me` : `${API}/advisors`;
      const method = editMode ? "patch" : "post";
      const r = await axios[method](url, fd, {
        headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(t("advisors_saved_pending"));
      navigate(`/professionals/${r.data.profile.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(t("advisors_delete_confirm"))) return;
    try {
      await axios.delete(`${API}/advisors/me`, auth);
      toast.success(t("advisors_deleted"));
      navigate("/professionals");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (bootstrapping) return <div className="min-h-screen bg-[#070709] text-white/40 flex items-center justify-center">Loading…</div>;

  return (
    <div dir={dir} className="min-h-screen bg-[#070709] text-white" data-testid="advisor-form-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070709] border-b border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate("/professionals")} className="flex items-center gap-2" data-testid="nav-logo-home"><Logo size="md" /></button>
          <div className="flex items-center gap-3">
            <LangSwitch />
            <button onClick={() => navigate("/professionals")} data-testid="form-nav-back"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white">
              <ArrowLeft size={16} /> {t("cancel")}
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-32 sm:pt-36 lg:pt-40 pb-16 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <p className="text-xs tracking-[0.3em] uppercase text-[#01AF9B]/80 mb-3">{t("advisors_eyebrow")}</p>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight mb-2">
          {editMode ? t("advisors_edit_title") : t("advisors_create_title")}
        </h1>
        <p className="text-white/60 mb-4">{t("advisors_form_subtitle")}</p>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="advisor-form">
          {/* IMAGE */}
          <section className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
            <label className="block text-sm font-semibold mb-3">{t("advisors_f_image")} *</label>
            <div className="flex items-center gap-6">
              <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-white/10 bg-white/5 flex items-center justify-center">
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" className="w-full h-full object-cover" data-testid="advisor-image-preview" />
                ) : (
                  <Camera size={40} className="text-white/30" />
                )}
              </div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                  onChange={onPickFile} className="hidden" data-testid="advisor-image-input" />
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="advisor-image-btn"
                  className="px-4 py-2 rounded-full bg-[#01AF9B] hover:bg-[#018F7E] text-white text-sm font-medium">
                  {imagePreview ? t("advisors_change_image") : t("advisors_upload_image")}
                </button>
                <p className="text-xs text-white/50 mt-2">{t("advisors_image_hint")}</p>
              </div>
            </div>
          </section>

          {/* IDENTITY */}
          <section className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 grid gap-4 sm:grid-cols-2">
            <TextField label={t("advisors_f_company_name")} value={form[K("company_name")]} onChange={v => set(K("company_name"), v)} required testid="f-company" />
            <div />
            <TextField label={t("advisors_f_first_name")} value={form.first_name} onChange={v => set("first_name", v)} required testid="f-first" />
            <TextField label={t("advisors_f_last_name")} value={form.last_name} onChange={v => set("last_name", v)} required testid="f-last" />
            <TextField label={t("advisors_f_email")} type="email" value={form.email} onChange={v => set("email", v)} required testid="f-email" />
            <TextField label={t("advisors_f_phone")} value={form.phone} onChange={v => set("phone", v)} required testid="f-phone" />
            <TextField label={t("advisors_f_city")} value={form[K("city")]} onChange={v => set(K("city"), v)} required testid="f-city" />
            <TextField label={t("advisors_f_country")} value={form.country} onChange={v => set("country", v)} required testid="f-country" />
          </section>

          {/* STORY */}
          <section className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 space-y-4">
            <TextField label={t("advisors_f_hero_message")} value={form[K("hero_message")]} onChange={v => set(K("hero_message"), v)} required testid="f-hero" maxLength={160} hint={t("advisors_hero_hint")} />
            <div>
              <label className="block text-sm font-semibold mb-2">{t("advisors_f_bio")} *</label>
              <textarea value={form[K("bio")]} onChange={e => set(K("bio"), e.target.value)} rows={6} data-testid="f-bio" maxLength={1600}
                placeholder={t("advisors_bio_placeholder")}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#01AF9B]/50" />
              <p className="text-xs text-white/50 mt-1">{(form[K("bio")] || "").length} / 1600</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label={t("advisors_f_website")} value={form.website} onChange={v => set("website", v)} testid="f-website" placeholder="https://..." />
              <TextField label={t("advisors_f_linkedin")} value={form.linkedin} onChange={v => set("linkedin", v)} testid="f-linkedin" placeholder="https://linkedin.com/in/..." />
            </div>
          </section>

          {/* TAGS */}
          <section className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
            <label className="block text-sm font-semibold mb-3">{t("advisors_f_services")} *</label>
            <p className="text-xs text-white/50 mb-4">{t("advisors_services_hint")}</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="advisor-tags-picker">
              {predefined.map(tg => {
                const on = form.service_tags.includes(tg);
                return (
                  <button type="button" key={tg} onClick={() => toggleTag(tg)}
                    data-testid={`tag-${tg.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                      on ? "bg-[#01AF9B] text-white border-[#01AF9B]" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                    }`}>{tg}</button>
                );
              })}
            </div>
            <div className="flex gap-2 mb-4">
              <input type="text" value={form.custom_tag} onChange={e => set("custom_tag", e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustomTag(); } }}
                placeholder={t("advisors_custom_tag_placeholder")} data-testid="advisor-custom-tag-input"
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#01AF9B]/50" />
              <button type="button" onClick={addCustomTag} data-testid="advisor-custom-tag-add"
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
                {t("advisors_add")}
              </button>
            </div>
            {form.service_tags.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="advisor-selected-tags">
                {form.service_tags.map(tg => (
                  <span key={tg} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#01AF9B]/15 border border-[#01AF9B]/30 text-[#01AF9B]">
                    {tg}
                    <button type="button" onClick={() => toggleTag(tg)} className="text-[#01AF9B]/70 hover:text-white" data-testid={`tag-remove-${tg.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </section>

          <div className="flex items-center justify-between pt-2">
            <div>
              {editMode && (
                <button type="button" onClick={onDelete} data-testid="advisor-form-delete"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 text-sm">
                  <Trash size={14} /> {t("advisors_delete")}
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => navigate("/professionals")} data-testid="advisor-form-cancel"
                className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
                {t("cancel")}
              </button>
              <button type="submit" disabled={submitting} data-testid="advisor-form-submit"
                className="px-6 py-2 rounded-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-semibold text-sm disabled:opacity-50">
                {submitting ? t("saving") : (editMode ? t("advisors_save") : t("advisors_create_submit"))}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testid, hint, maxLength, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-2">{label}{required && " *"}</label>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
        maxLength={maxLength} placeholder={placeholder} data-testid={testid}
        className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#01AF9B]/50" />
      {hint && <p className="text-xs text-white/50 mt-1">{hint}</p>}
    </div>
  );
}
